import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appConfig } from "../api";
import logo from "../assets/img/elite-logo.png";

const currency = (value) =>
  `Rs. ${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString("en-IN");
};

const getStoredInvoiceBooking = (search) => {
  const params = new URLSearchParams(search);
  const key = params.get("key");

  if (!key) return null;

  try {
    const rawBooking = window.localStorage.getItem(`invoice:${key}`);
    return rawBooking ? JSON.parse(rawBooking) : null;
  } catch (error) {
    console.error("Unable to load invoice booking:", error);
    return null;
  }
};

const Invoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const booking = location.state?.booking || getStoredInvoiceBooking(location.search);

  const totals = useMemo(() => ({ amount: Number(booking?.amount || 0) }), [booking]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!booking || params.get("print") !== "1") return undefined;

    const printTimer = window.setTimeout(() => {
      window.print();
    }, 400);

    return () => window.clearTimeout(printTimer);
  }, [booking, location.search]);

  if (!booking) {
    return (
      <main className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
        <section className="bg-white border border-slate-200 rounded-lg p-8 max-w-md text-center shadow-sm">
          <h1 className="text-xl font-semibold text-slate-900">Invoice unavailable</h1>
          <p className="text-sm text-slate-500 mt-2">
            Please open the invoice again from the bookings page.
          </p>
          <button
            type="button"
            onClick={() => navigate("/adminhome")}
            className="mt-5 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium"
          >
            Back to Admin
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-100 py-8 px-4 print:bg-white print:p-0">
      <style>{`
        @page {
          size: A4;
          margin: 12mm;
        }

        @media print {
          .no-print {
            display: none !important;
          }

          .invoice-sheet {
            box-shadow: none !important;
            border: 0 !important;
            width: 100% !important;
            max-width: none !important;
            padding: 0 !important;
          }

          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>

      <div className="no-print max-w-4xl mx-auto mb-4 flex justify-between items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/adminhome")}
          className="px-4 py-2 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="px-5 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
        >
          Print Invoice
        </button>
      </div>

      <section className="invoice-sheet max-w-4xl mx-auto bg-white border border-slate-200 shadow-sm p-8 text-slate-900">
        <header className="flex items-start justify-between border-b border-slate-200 pb-5 gap-6">
          <div className="flex items-start gap-4">
            <img src={logo} alt={appConfig.hotel.name} className="w-20 h-20 object-contain shrink-0" />
            <div>
              <h1 className="text-2xl font-bold tracking-wide">{appConfig.hotel.legalName}</h1>
              <p className="text-sm font-medium text-slate-600">{appConfig.hotel.name}</p>
              <p className="text-xs text-slate-500 max-w-xl mt-1">{appConfig.hotel.address}</p>
              <p className="text-xs text-slate-500 mt-1">Phone: {appConfig.hotel.invoicePhone}</p>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p className="font-semibold text-slate-900 text-base">Invoice</p>
            <p>{booking.bookingId || "N/A"}</p>
            <p>{formatDate(new Date())}</p>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 py-6 border-b border-slate-200">
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Guest</h2>
            <p className="font-semibold">{booking.customerName || "N/A"}</p>
            <p className="text-sm text-slate-600">{booking.phoneNumber || "N/A"}</p>
            <p className="text-sm text-slate-600">{booking.email || ""}</p>
            <p className="text-sm text-slate-600">{booking.address || "N/A"}</p>
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Stay Details</h2>
            <div className="grid grid-cols-2 gap-y-1 text-sm">
              <span className="text-slate-500">Check-In</span>
              <span className="font-medium">{formatDate(booking.checkInDate)}</span>
              <span className="text-slate-500">Check-Out</span>
              <span className="font-medium">{formatDate(booking.checkOutDate)}</span>
              <span className="text-slate-500">Room Type</span>
              <span className="font-medium">{booking.roomType || "N/A"}</span>
              <span className="text-slate-500">Meal Plan</span>
              <span className="font-medium">{booking.mealPlan || "N/A"}</span>
              <span className="text-slate-500">Guests</span>
              <span className="font-medium">
                {Number(booking.adults || 0)} adult(s), {Number(booking.kids || 0)} kid(s)
              </span>
            </div>
          </div>
        </section>

        <section className="py-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-900 text-white">
                <th className="text-left p-3 font-semibold">Description</th>
                <th className="text-right p-3 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-200">
                <td className="p-3">Room Tariff</td>
                <td className="p-3 text-right">{currency(totals.amount)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td className="p-3 text-right font-bold text-base">Total</td>
                <td className="p-3 text-right font-bold text-base">{currency(totals.amount)}</td>
              </tr>
            </tfoot>
          </table>
        </section>

        <footer className="border-t border-slate-200 pt-5 text-xs text-slate-500">
          <p>
            All payments are to be made by CASH/CARD/NEFT/UPI in the name of "{appConfig.hotel.paymentName}".
          </p>
          <p className="mt-1">For invoice queries, please contact {appConfig.hotel.email}.</p>
          <p className="mt-3 italic">We will do our best. Thank you for your business.</p>
        </footer>
      </section>
    </main>
  );
};

export default Invoice;
